/**
 * Firestore `logEntries` shapes as the mobile app actually writes them
 * (LogEntryActions / QuickMacrosActions / Mood.tsx / Symptom.tsx / Supplement.tsx / AddExercise.tsx).
 */
export const LOG_CATEGORY = {
  FOOD: 0,
  MOOD: 1,
  SYMPTOM: 2,
  MEDICATION: 3,
  EXERCISE: 4,
  WATER: 5,
  NOTE: 6,
} as const
export type LogCategory = (typeof LOG_CATEGORY)[keyof typeof LOG_CATEGORY]

export interface BaseEntry {
  id: string
  uid: string
  cat: LogCategory
  date: string // YYYY-MM-DD (local)
  time: string // ISO
}

export type FoodEntryType = 'api' | 'user' | 'recipe' | 'userrecipe' | 'macro' | 'edamam' | 'aiscan' | 'vore' | 'vorerecipe' | 'sharedrecipe' | string

export interface AIIngredient {
  food_name: string
  serving_qty: number
  serving_unit: string
  serving_weight_grams?: number
  calories: number
  fats: number
  proteins: number
  totalcarbs: number
  fiber: number
  nutrients?: Record<string, number>
  diet_classification?: string
}

export interface FoodEntry extends BaseEntry {
  cat: 0
  food_id: string
  food_name: string
  brand_name?: string
  serving_desc: string
  calories: number
  fats: number
  proteins: number
  totalcarbs: number
  fiber: number
  nutrients?: Record<string, number>
  type: FoodEntryType
  random: string
  notes?: string
  image_url?: string
  isAIScan?: boolean
  source?: 'camera' | 'voice' | 'label'
  ingredients?: AIIngredient[]
}

export interface MoodEntry extends BaseEntry {
  cat: 1
  mood: 0 | 1 | 2 | 3 | 4
  notes?: string
}

export interface SymptomEntry extends BaseEntry {
  cat: 2
  symptom: { id: number | string; label: string }
  severity: number // 0-4
  notes?: string
}

export interface MedEntry extends BaseEntry {
  cat: 3
  med: { id: number | string; label: string }
  qty: number
  units: string
  notes?: string
}

export interface ExerciseEntry extends BaseEntry {
  cat: 4
  value: number // kcal burned (always stored in kCal)
  name?: string
  duration?: number
  notes?: string
  metadata?: { healthkit_uuid?: string; source?: string; workout_type?: string }
}

export interface WaterEntry extends BaseEntry {
  cat: 5
  value: number // ml (always stored metric)
}

export interface NoteEntry extends BaseEntry {
  cat: 6
  value: string
}

export type LogEntry = FoodEntry | MoodEntry | SymptomEntry | MedEntry | ExerciseEntry | WaterEntry | NoteEntry

/** presets/{uid}/meal/{food_id} */
export interface FoodPreset {
  food_id: string
  food_name: string
  brand_name: string
  serving_size: string
  uid: string
  type: 'user' | 'aiscan' | string
  calories: number
  totalcarbs: number
  fiber: number
  proteins: number
  fats: number
  nutrients?: Record<string, number>
  image_url?: string
  isAIScan?: boolean
  ingredients?: AIIngredient[]
  source?: string
}

/** favorites/{uid} and recentlyAdded/{uid} map values */
export interface QuickFoodItem {
  food_id: string
  food_name: string
  brand_name: string
  type: string
  calories: number
  fats: number
  proteins: number
  totalcarbs: number
  fiber: number
  serving_size?: string
  timestamp: string
  nutrients?: Record<string, number>
  isAIScan?: boolean
  ingredients?: AIIngredient[]
  image_url?: string
  source?: string
}

/** recipes/{uid}/recipes/{food_id} */
export interface UserRecipeIngredient {
  id?: string
  food_id?: string
  food_name: string
  brand_name?: string
  serving_desc?: string
  calories: number
  fats: number
  proteins: number
  totalcarbs: number
  fiber: number
  nutrients?: Record<string, number>
}

export interface UserRecipeDoc {
  food_id: string
  food_name: string
  brand_name: string
  description?: string
  image_url?: string
  ingredients?: UserRecipeIngredient[]
  directions?: string[]
  serving_size?: string
  serving_yield?: number
  calories: number
  fats: number
  proteins: number
  totalcarbs: number
  fiber: number
  nutrients?: Record<string, number>
  type: 'recipe' | 'userrecipe' | string
  uid?: string
  timestamp?: string
  isShared?: boolean
  publicRecipeId?: string | null
}

export interface MacroSet {
  calories: number
  fats: number
  proteins: number
  totalcarbs: number
  fiber: number
  nutrients?: Record<string, number>
}
